import { Anchor, Group, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { SortingState } from '@tanstack/react-table';
import {
	createFacetedMinMaxValues,
	createFacetedUniqueValues,
	useTable,
} from '@tanstack/react-table';
import dayjs from 'dayjs';
import { createElement, useCallback, useMemo, useState } from 'react';

import { ConfidentialStatusSwitch, ErrorIcon } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { createFilterOnlyColumn } from '@apple/ui/data-table/utils/filters';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { ServerValidationError } from '@apple/utils/api/errors';
import { useFileDownload } from '@apple/utils/files/hooks/useFileDownload';
import { FormattedBoolean } from '@apple/utils/globalization/components/FormattedBoolean';
import { FormattedDate } from '@apple/utils/globalization/components/FormattedDate';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { updateConfidentialOrder } from '@apple/features/order/api/distro';
import type {
	DistroHistoryFilters,
	DistroHistoryListing,
} from '@apple/features/order/models/distro';
import { getDistroHistoryQueryOptions } from '@apple/features/order/queries/distro';
import { getDistroOrderHistoryDownloadUrl } from '@apple/features/order-channel/api/distro';

const features = createDataTableFeatures({
	facetedUniqueValues: createFacetedUniqueValues(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

const defaultSorting: SortingState = [{ id: 'orderData', desc: true }];
const defaultFilters: DistroHistoryFilters = {
	orderDateRangeInDays: 60,
};
export const Route = createFileRoute('/_authed/_admin/distro/history')({
	component: DistroHistoryRoute,
	beforeLoad: args =>
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.DistroOrderHistory',
		}),
});

function DistroHistoryRoute() {
	const { t } = useTranslation('distro-history');
	const queryClient = useQueryClient();
	const [loading, setLoading] = useState(false);
	const search = Route.useSearch();
	const navigate = Route.useNavigate();

	const distroBulkOrderDownload = useFileDownload({
		method: 'post',
		url: '',
		fallbackFilename: `DistroOrderExport_${dayjs().format('YYYY-MM-DD')}.xlsx`,
		errorTitle: t('common:error.title'),
		errorMessage: t('common:error.fileDownload'),
	});

	const handleOrderClick = useCallback(
		async (salesOrderNumber: string) => {
			await distroBulkOrderDownload.download({
				url: getDistroOrderHistoryDownloadUrl(salesOrderNumber),
			});
		},
		[distroBulkOrderDownload],
	);

	const changeOrderConfidentialState = useMutation({
		mutationFn: (order: DistroHistoryListing) => {
			setLoading(true);
			return updateConfidentialOrder(order.salesOrderNumber, {
				key: 'IsGhostShipment',
				value: !order.isGhostOrder,
			});
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: [
					'distro-history',
					tableState.currentFilterData,
					tableState.state.pagination,
				],
			});
			setLoading(false);
		},
		onError: (error: Error) => {
			if (!(error instanceof ServerValidationError)) {
				notifications.show({
					message: t('common:error.generic'),
					icon: <ErrorIcon />,
					color: 'red',
					autoClose: 5000,
				});
			}
		},
	});

	const minDate = useMemo(() => {
		return dayjs().subtract(4, 'year').toDate();
	}, []);
	const today = useMemo(() => {
		return dayjs().toDate();
	}, []);

	const tableState = useTableState<DistroHistoryListing, DistroHistoryFilters>({
		search,
		navigate,
		defaultFilters,
		defaultSorting,
		fieldMap: [],
	});

	const distroHistoryQuery = useQuery(
		getDistroHistoryQueryOptions(
			tableState.state.pagination,
			tableState.currentFilterData,
			_ => {
				notifications.show({
					color: 'red',
					message: t('common:error.fetchOrders'),
					icon: createElement(ErrorIcon),
				});
			},
		),
	);

	const table = useTable({
		features,
		data: distroHistoryQuery.data.rows,
		rowCount: distroHistoryQuery.data.totalRowCount,
		...tableState,
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: true,
		manualFiltering: true,
		manualSorting: true,
		manualPagination: true,
		meta: {
			getFacetedUniqueValues: columnId => {
				switch (columnId) {
					case 'orderDateRangeInDays':
						return new Map([
							['30', 1],
							['60', 1],
							['90', 1],
							['custom', 1],
						]);
					default:
						return undefined;
				}
			},
		},
		columnResizeMode: 'onChange',
		columns: [
			{
				header: t('searchResults.table.salesOrderNumber'),
				accessorKey: 'orderNumber',
				filter: {
					group: t('filters.groups.labels.allFilters'),
				},
				cell: ({ row }) => (
					<Anchor onClick={() => void handleOrderClick(row.original.salesOrderNumber)}>
						{row.original.salesOrderNumber}
					</Anchor>
				),
			},
			createFilterOnlyColumn<DistroHistoryListing, DistroHistoryFilters>({
				group: t('filters.groups.labels.allFilters'),
				label: t('filters.labels.orderDate'),
				field: 'orderDateRangeInDays',
				variant: 'select',
				getFilterDisplayValue: value =>
					value === 'custom'
						? t('controls:dataTable.filters.options.dateRange.custom')
						: t('controls:dataTable.filters.options.dateRange.lastNumberOfDays', {
								days: value,
							}),
			}),
			createFilterOnlyColumn<DistroHistoryListing, DistroHistoryFilters>({
				group: t('filters.groups.labels.allFilters'),
				label: t('filters.labels.startDate'),
				field: 'startDate',
				variant: 'date',
				hidden: tableState.currentFilterData.orderDateRangeInDays !== 'custom',
				minValue: minDate,
				maxValue: tableState.currentFilterData.endDate ?? today,
			}),
			createFilterOnlyColumn<DistroHistoryListing, DistroHistoryFilters>({
				group: t('filters.groups.labels.allFilters'),
				label: t('filters.labels.endDate'),
				field: 'endDate',
				variant: 'date',
				hidden: tableState.currentFilterData.orderDateRangeInDays !== 'custom',
				minValue: tableState.currentFilterData.startDate ?? minDate,
				maxValue: today,
			}),
			{
				header: t('searchResults.table.userName'),
				accessorKey: 'userName',
				enableColumnFilter: false,
			},
			createFilterOnlyColumn<DistroHistoryListing, DistroHistoryFilters>({
				group: t('filters.groups.labels.allFilters'),
				label: t('filters.labels.userName'),
				field: 'userId',
				variant: 'text',
			}),
			{
				header: t('searchResults.table.projectName'),
				accessorKey: 'projectName',
				filter: {
					group: t('filters.groups.labels.allFilters'),
				},
				cell: ({ row }) => <Text>{row.original.orderName}</Text>,
			},
			{
				header: t('searchResults.table.ghostOrder'),
				accessorKey: 'isGhostOrder',
				cell: ({ row }) => (
					<Group>
						<FormattedBoolean value={row.original.isGhostOrder} />
						{row.original.isGhostOrder ? (
							<ConfidentialStatusSwitch
								c='red'
								disabled={loading}
								loading={loading}
								tooltip={t('buttons.unMarkConfidential')}
								onClick={() =>
									void changeOrderConfidentialState.mutateAsync(row.original)
								}
							/>
						) : (
							<ConfidentialStatusSwitch
								c='green'
								disabled={loading}
								loading={loading}
								tooltip={t('buttons.markConfidential')}
								onClick={() =>
									void changeOrderConfidentialState.mutateAsync(row.original)
								}
							/>
						)}
					</Group>
				),
				enableColumnFilter: false,
			},
			{
				header: t('searchResults.table.orderDate'),
				accessorKey: 'orderDate',
				cell: ({ row }) => <FormattedDate value={row.original.orderDate} humanize={true} />,
				filter: {
					group: t('filters.groups.labels.allFilters'),
					variant: 'date-range',
				},
				enableColumnFilter: false,
			},
			{
				header: t('searchResults.table.submitted'),
				accessorKey: 'isSubmitted',
				cell: ({ row }) => <FormattedBoolean value={row.original.isSubmitted} />,
				enableColumnFilter: false,
			},
			{
				header: t('searchResults.table.comments'),
				accessorKey: 'comments',
				filter: {
					group: t('filters.groups.labels.allFilters'),
				},
			},
			{
				header: t('searchResults.table.totalLinesItemsCount'),
				accessorKey: 'totalLineItemsCount',
				enableColumnFilter: false,
			},
			{
				header: t('searchResults.table.cancelled'),
				accessorKey: 'isCancelled',
				cell: ({ row }) => <FormattedBoolean value={row.original.isCancelled} />,
				enableColumnFilter: false,
			},
			{
				header: t('searchResults.table.totalOrdersCount'),
				accessorKey: 'totalOrdersCount',
				enableColumnFilter: false,
			},
		],
	});

	return (
		<TableLayout title={t('searchResults.title')} table={table}>
			<DataTable table={table} loading={distroHistoryQuery.isFetching} />
		</TableLayout>
	);
}
